<template>
  <aside
    class="three-d-part-card box"
    data-unit="three-d-part-details-card"
  >
    <button
      class="delete close-card"
      type="button"
      :aria-label="$t('close')"
      data-unit="close-three-d-part-card"
      @click="$emit('close')"
    />

    <div
      v-if="status === 'loading'"
      role="status"
      aria-live="polite"
      data-unit="part-mapping-loading"
    >
      <loading-icon />
    </div>

    <div
      v-else-if="status === 'unavailable'"
      role="status"
      aria-live="polite"
      data-unit="part-mapping-unavailable"
    >
      <p class="has-text-weight-semibold">
        {{ $t('threeD.partInformationUnavailable') }}
      </p>
      <p>{{ $t('threeD.partInformationUnavailableHelp') }}</p>
    </div>

    <div
      v-else-if="status === 'error'"
      role="alert"
      data-unit="part-mapping-error"
    >
      <p>{{ $t('threeD.partInformationError') }}</p>
      <button
        class="button is-small"
        type="button"
        data-unit="retry-part-mapping"
        @click="$emit('retry')"
      >
        {{ $t('tryAgain') }}
      </button>
    </div>

    <template v-else-if="status === 'ready' && part">
      <p
        class="has-text-weight-semibold part-name"
        data-unit="mapped-part-name"
      >
        {{ part.name || part.displayName }}
      </p>
      <p
        v-if="showPartNumber"
        class="part-identity"
        data-unit="mapped-part-number"
      >
        <span>{{ $t('partNumber') }}: {{ part.partNumber }}</span>
        <button
          class="button is-white is-small copy-button"
          type="button"
          :aria-label="$t('copy')"
          @click="copyPartNumber(part.partNumber)"
        >
          <span class="icon far fa-copy" />
        </button>
      </p>
      <p
        v-if="showSupplier"
        data-unit="mapped-part-supplier"
      >
        {{ $t('supplier') }}: {{ part.supplierName || part.supplierKey }}
      </p>
      <info-add-to-cart
        v-if="showOrdering"
        :key="orderingPartKey"
        :add-to-cart-qty-behavior="addToCartQtyBehavior"
        :content="part"
        :display-availability="displayAvailability"
        :display-discounted="displayDiscounted"
        :display-eta="displayEta"
        :display-retail="displayRetail"
        :display-whole-sale="displayWholeSale"
        :is-cart-or-erp-cart-enabled="isCartOrErpCartEnabled"
        :pricing="pricing"
        show-pricing-for-parts
      />
    </template>

    <template v-else-if="status === 'ready'">
      <p
        v-if="element.partNumber"
        class="part-identity"
        data-unit="unmapped-part-number"
      >
        <span>{{ $t('partNumber') }}: {{ element.partNumber }}</span>
        <button
          class="button is-white is-small copy-button"
          type="button"
          :aria-label="$t('copy')"
          @click="copyPartNumber(element.partNumber)"
        >
          <span class="icon far fa-copy" />
        </button>
      </p>
      <p data-unit="unmapped-part-message">
        {{ $t('threeD.callForAvailabilityAndPricing') }}
      </p>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import InfoAddToCart from './info/InfoAddToCart.vue'
import LoadingIcon from '@/components/common/LoadingIcon.vue'
import type { ViewerSelectedModelElement } from '@/components/admin/content/editor/three-d-model/AutodeskViewer.vue'
import type { Part } from '@/interfaces/library'

type MappedPart = Part & {
  displayName?: string
  partNumberVisible?: boolean
  supplierName?: string
  visible?: boolean
}

const props = defineProps<{
  element: ViewerSelectedModelElement
  part: MappedPart | null
  pricingLoaded: boolean
  status: 'error' | 'idle' | 'loading' | 'ready' | 'unavailable'
}>()

defineEmits(['close', 'retry'])

const store = useStore()
const isVisible = computed(() => props.part?.visible !== false)
const showPartNumber = computed(() =>
  isVisible.value &&
  props.part?.partNumberVisible !== false &&
  !!props.part?.partNumber
)
const showSupplier = computed(() =>
  isVisible.value &&
  store.getters['user/displaySupplierPartInfo'] &&
  !!(props.part?.supplierName || props.part?.supplierKey)
)
const showOrdering = computed(() => isVisible.value && props.part?.orderable === true)
const orderingPartKey = computed(() => {
  const part = props.part
  return part?.id != null
    ? `id:${part.id}`
    : `part:${part?.supplierKey ?? ''}:${part?.partNumber ?? ''}`
})
const pricing = computed(() => ({ ...props.part, isLoaded: props.pricingLoaded }))
const addToCartQtyBehavior = computed(() => store.state.user.preferences.addToCartQtyBehavior)
const displayAvailability = computed(() => store.getters['user/displayAvailability'])
const displayDiscounted = computed(() => store.getters['user/displayDiscounted'])
const displayEta = computed(() => store.getters['user/displayEta'])
const displayRetail = computed(() => store.getters['user/displayRetail'])
const displayWholeSale = computed(() => store.getters['user/displayWholeSale'])
const isCartOrErpCartEnabled = computed(() => store.getters['user/isCartOrErpCartEnabled'])

function copyPartNumber (partNumber?: string | null) {
  if (partNumber) navigator.clipboard.writeText(partNumber)
}
</script>

<style scoped>
.three-d-part-card {
  font-size: 0.875rem;
  max-height: calc(100% - 1.5rem);
  overflow: auto;
  padding: 1rem;
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  width: min(21rem, calc(100% - 1.5rem));
  z-index: 4;
}
.close-card {
  float: right;
}
.part-name {
  font-size: 1rem;
  padding-right: 1.5rem;
}
.part-identity {
  align-items: center;
  display: flex;
}
.copy-button {
  margin-left: 0.25rem;
  padding: 0;
}
</style>
